import { useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  LocalStorageKeys,
  useLocalStorage,
} from "@/components/pages/login/hooks/useLocalStorage";
import { useServices } from "@/hooks/useServices";
import { authService, LoginResponse } from "@/services/authService";
import {
  decodeJwtPayload,
  getAdminImpersonationExitPath,
  getHotelIdFromJwtPayload,
  getImpersonatorIdFromJwtPayload,
  getTenantIdFromJwtPayload,
  isSuperAdminJwtPayload,
  isTenantAdminJwtPayload,
} from "@/utils/authSessionUser";

interface AdminSession {
  accessToken: string;
  refreshToken: string | null;
  tenantId: string | null;
  hotelId: string | null;
}

interface UseAdminImpersonationReturn {
  isImpersonating: boolean;
  isStartingImpersonation: boolean;
  impersonateUser: (userId: string) => Promise<void>;
  exitImpersonation: () => void;
}

const getStorageValue = (key: LocalStorageKeys): string | null =>
  localStorage.getItem(key);

const setStorageValue = (key: LocalStorageKeys, value: string | null): void => {
  if (value) {
    localStorage.setItem(key, value);
    return;
  }

  localStorage.removeItem(key);
};

const getStoredOriginalAdminSession = (): AdminSession | null => {
  const accessToken = getStorageValue(LocalStorageKeys.ADMIN_ACCESS_TOKEN);

  if (!accessToken) {
    return null;
  }

  return {
    accessToken,
    refreshToken: getStorageValue(LocalStorageKeys.ADMIN_REFRESH_TOKEN),
    tenantId: getStorageValue(LocalStorageKeys.ADMIN_TENANT_ID),
    hotelId: getStorageValue(LocalStorageKeys.ADMIN_HOTEL_ID),
  };
};

const getCurrentSession = (): AdminSession | null => {
  const accessToken = getStorageValue(LocalStorageKeys.ACCESS_TOKEN);

  if (!accessToken) {
    return null;
  }

  const payload = decodeJwtPayload(accessToken);

  return {
    accessToken,
    refreshToken: getStorageValue(LocalStorageKeys.REFRESH_TOKEN),
    tenantId:
      getStorageValue(LocalStorageKeys.TENANT_ID) ??
      (payload ? getTenantIdFromJwtPayload(payload) : null),
    hotelId:
      getStorageValue(LocalStorageKeys.HOTEL_ID) ??
      (payload ? getHotelIdFromJwtPayload(payload) : null),
  };
};

const getErrorStatus = (error: unknown): number | null => {
  if (typeof error !== "object" || error === null) {
    return null;
  }

  const record = error as Partial<Record<"statusCode" | "status", unknown>>;
  const status = record.statusCode ?? record.status;

  return typeof status === "number" ? status : null;
};

const getErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error && error.message.length > 0) {
    return error.message;
  }

  return null;
};

const getImpersonatedHotelId = (
  response: LoginResponse,
  payload: Record<string, unknown> | null,
): string | null =>
  (payload ? getHotelIdFromJwtPayload(payload) : null) ??
  response.user.hotelId ??
  response.user.hotelIds?.[0] ??
  null;

const isCurrentSessionImpersonated = (): boolean => {
  const accessToken = getStorageValue(LocalStorageKeys.ACCESS_TOKEN);
  const payload = accessToken ? decodeJwtPayload(accessToken) : null;

  return Boolean(payload && getImpersonatorIdFromJwtPayload(payload));
};

export const useAdminImpersonation = (): UseAdminImpersonationReturn => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { setAuthToken } = useServices();
  const {
    setAccessToken,
    setRefreshToken,
    setTenantId,
    setHotelId,
    setAdminAccessToken,
    setAdminRefreshToken,
  } = useLocalStorage();

  const storeOriginalAdminSession = useCallback(
    (session: AdminSession) => {
      setAdminAccessToken(session.accessToken);
      setAdminRefreshToken(session.refreshToken);
      setStorageValue(LocalStorageKeys.ADMIN_TENANT_ID, session.tenantId);
      setStorageValue(LocalStorageKeys.ADMIN_HOTEL_ID, session.hotelId);
    },
    [setAdminAccessToken, setAdminRefreshToken],
  );

  const clearOriginalAdminSession = useCallback(() => {
    setAdminAccessToken(null);
    setAdminRefreshToken(null);
    setStorageValue(LocalStorageKeys.ADMIN_TENANT_ID, null);
    setStorageValue(LocalStorageKeys.ADMIN_HOTEL_ID, null);
  }, [setAdminAccessToken, setAdminRefreshToken]);

  const applySession = useCallback(
    (
      accessToken: string,
      refreshToken: string | null,
      tenantId: string | null,
      hotelId: string | null,
      redirectPath: string,
    ) => {
      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      setTenantId(tenantId);
      setHotelId(hotelId);
      setAuthToken(accessToken);
      queryClient.clear();
      window.location.replace(redirectPath);
    },
    [
      queryClient,
      setAccessToken,
      setAuthToken,
      setHotelId,
      setRefreshToken,
      setTenantId,
    ],
  );

  const getImpersonationErrorMessage = useCallback(
    (error: unknown): string => {
      const status = getErrorStatus(error);

      if (status === 401) {
        return t(
          "tenants.detail.users.loginAsUserUnavailable",
          "Impersonation is currently unavailable for this admin account. Please contact support.",
        );
      }

      if (status === 403) {
        return t(
          "tenants.detail.users.loginAsUserForbidden",
          "You do not have permission to impersonate this user.",
        );
      }

      if (status === 404) {
        return t(
          "tenants.detail.users.loginAsUserNotFound",
          "The selected user was not found.",
        );
      }

      return (
        getErrorMessage(error) ??
        t(
          "tenants.detail.users.loginAsUserError",
          "Failed to start impersonation.",
        )
      );
    },
    [t],
  );

  const impersonationMutation = useMutation({
    mutationFn: async (userId: string) => {
      const currentSession = getCurrentSession();
      const payload = currentSession
        ? decodeJwtPayload(currentSession.accessToken)
        : null;

      if (!currentSession || !payload) {
        throw new Error(
          t(
            "tenants.detail.users.loginAsUserError",
            "Failed to start impersonation.",
          ),
        );
      }

      if (
        !isTenantAdminJwtPayload(payload) &&
        !isSuperAdminJwtPayload(payload)
      ) {
        throw new Error(
          t(
            "tenants.detail.users.loginAsUserForbidden",
            "You do not have permission to impersonate this user.",
          ),
        );
      }

      const originalAdminSession =
        getStoredOriginalAdminSession() ?? currentSession;
      const response = isTenantAdminJwtPayload(payload)
        ? await authService.tenantAdminLoginAsUser({ userId })
        : await authService.loginAsUser({ userId });
      const impersonatedPayload = decodeJwtPayload(response.accessToken);

      return {
        response,
        originalAdminSession,
        hotelId: getImpersonatedHotelId(response, impersonatedPayload),
        tenantId:
          response.user.tenantId ??
          (impersonatedPayload
            ? getTenantIdFromJwtPayload(impersonatedPayload)
            : null),
      };
    },
    onSuccess: ({ response, originalAdminSession, hotelId, tenantId }) => {
      storeOriginalAdminSession(originalAdminSession);
      toast.success(t("tenants.detail.users.loginAsUserSuccess"));

      applySession(
        response.accessToken,
        response.refreshToken,
        tenantId,
        hotelId,
        "/",
      );
    },
    onError: (error) => {
      toast.error(getImpersonationErrorMessage(error));
    },
  });

  const exitImpersonation = useCallback(() => {
    const originalSession = getStoredOriginalAdminSession();

    if (!originalSession) {
      return;
    }

    const payload = decodeJwtPayload(originalSession.accessToken);
    const exitPath = payload ? getAdminImpersonationExitPath(payload) : null;

    if (!payload || !exitPath) {
      clearOriginalAdminSession();
      toast.error(t("layout.userMenu.exitImpersonationError"));
      return;
    }

    clearOriginalAdminSession();
    toast.success(t("layout.userMenu.exitImpersonationSuccess"));
    applySession(
      originalSession.accessToken,
      originalSession.refreshToken,
      originalSession.tenantId ?? getTenantIdFromJwtPayload(payload),
      originalSession.hotelId,
      exitPath,
    );
  }, [applySession, clearOriginalAdminSession, t]);

  return {
    isImpersonating:
      isCurrentSessionImpersonated() ||
      Boolean(getStoredOriginalAdminSession()),
    isStartingImpersonation: impersonationMutation.isPending,
    impersonateUser: async (userId: string) => {
      await impersonationMutation.mutateAsync(userId);
    },
    exitImpersonation,
  };
};
